import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Send, Square, Paperclip, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, Loader2, Zap, Database,
  FileJson, Terminal, Upload, X, ThumbsUp, ThumbsDown,
  Copy, Check, Table, Download, Globe2, Server, Wrench,
  AlertTriangle, FileCode2, Info
} from 'lucide-react'
import { useChatStream, ChatMessage, ToolEvent } from '../../hooks/useChatStream'
import { apiGet, apiPost } from '../../lib/api'
import { useDropzone } from 'react-dropzone'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'

interface ChatViewProps {
  conversationId: string
  projectName: string
  project?: {
    id: string
    name?: string
    target_system?: string
    connection_name?: string
    saviynt_url?: string
    saviynt_username?: string
    secret_count?: number
  } | null
  conversationTitle?: string
  configStatus?: string
  onConfigUpdate?: (status: string) => void
  onFirstMessage?: (message: string) => void
}

// ─── Code block with copy button ─────────────────────────────────────────────

function CodeBlock({ language, children }: { language: string; children: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(children).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }
  return (
    <div className="code-block-wrapper">
      <button className={`copy-btn${copied ? ' copied' : ''}`} onClick={handleCopy} title="Copy code" aria-label={copied ? 'Copied' : 'Copy code'}>
        {copied ? <Check size={12} /> : <Copy size={12} />}
        <span>{copied ? 'Copied!' : 'Copy'}</span>
      </button>
      <SyntaxHighlighter style={oneDark as any} language={language} PreTag="div" wrapLongLines>
        {children}
      </SyntaxHighlighter>
    </div>
  )
}

// ─── Markdown components ──────────────────────────────────────────────────────

// Full renderer — used once streaming is complete. Includes Prism syntax
// highlighting and the copy button.
const markdownComponents = {
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '')
    return !inline && match ? (
      <CodeBlock language={match[1]}>{String(children).replace(/\n$/, '')}</CodeBlock>
    ) : (
      <code className="inline-code" {...props}>{children}</code>
    )
  },
  table({ children }: any) {
    return <div className="table-wrap"><table>{children}</table></div>
  },
  a({ href, children }: any) {
    return (
      <a href={href} target="_blank" rel="noreferrer"
         className="text-green-400 underline hover:text-green-300 transition-colors">
        {children}
      </a>
    )
  }
}

// Lightweight renderer — used while streaming. Skips Prism and the copy button
// so each RAF flush doesn't re-run the syntax highlighter over a growing block.
const streamingMarkdownComponents = {
  ...markdownComponents,
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '')
    return !inline && match ? (
      <div className="code-block-wrapper">
        <pre><code className={className}>{String(children).replace(/\n$/, '')}</code></pre>
      </div>
    ) : (
      <code className="inline-code" {...props}>{children}</code>
    )
  }
}

// ─── Tool Activity Panel ──────────────────────────────────────────────────────

const ToolActivity = React.memo(function ToolActivity({ tools }: { tools: ToolEvent[] }) {
  const [expanded, setExpanded] = useState<string | null>(null)
  if (!tools.length) return null

  const toolIcons: Record<string, React.ReactNode> = {
    authenticate_saviynt:    <Zap size={13} />,
    get_connection_details:  <Database size={13} />,
    list_connections:        <Database size={13} />,
    test_connection:         <Terminal size={13} />,
    save_connection:         <CheckCircle2 size={13} />,
    create_connection:       <CheckCircle2 size={13} />,
    deploy_via_terraform:    <Terminal size={13} />,
    rollback_connection:     <Terminal size={13} />,
    web_search:              <Terminal size={13} />,
    ingest_document:         <Database size={13} />,
  }

  const toolLabels: Record<string, string> = {
    authenticate_saviynt:    'Authenticating to Saviynt',
    get_connection_details:  'Fetching connection details',
    list_connections:        'Listing connections',
    test_connection:         'Testing connection',
    save_connection:         'Saving connection',
    create_connection:       'Creating connection',
    deploy_via_terraform:    'Deploying via Terraform',
    rollback_connection:     'Rolling back connection',
    web_search:              'Searching the web',
    ingest_document:         'Ingesting document',
    upload_schema_file:      'Upload Schema File',
  }

  return (
    <div className="tool-panel">
      {tools.map((tool, i) => {
        const key = `${tool.name}-${i}`
        const isExpanded = expanded === key
        const success = tool.result?.success as boolean | undefined
        const isPending = tool.type === 'tool_call' && !tool.result

        return (
          <motion.div
            key={key}
            className={`tool-item ${isPending ? 'pending' : success === false ? 'failed' : 'success'}`}
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <button className="tool-header" onClick={() => setExpanded(isExpanded ? null : key)} aria-label={isExpanded ? 'Collapse tool details' : 'Expand tool details'}>
              <span className="tool-icon">
                {isPending ? <Loader2 size={13} className="spin" />
                  : success === false ? <XCircle size={13} />
                    : toolIcons[tool.name] || <Terminal size={13} />}
              </span>
              <span className="tool-name">{toolLabels[tool.name] ?? tool.name.replace(/_/g, ' ')}</span>
              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            <AnimatePresence>
              {isExpanded && (
                <motion.div className="tool-detail"
                  initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}>
                  {tool.input && (
                    <div className="tool-section">
                      <span className="tool-label">Input</span>
                      <pre>{JSON.stringify(
                        Object.fromEntries(Object.entries(tool.input).map(([k, v]) =>
                          ['password', 'token', 'saviynt_token'].includes(k) ? [k, '***'] : [k, v]
                        )), null, 2
                      )}</pre>
                    </div>
                  )}
                  {tool.result && (
                    <div className="tool-section">
                      <span className="tool-label">Result</span>
                      <pre>{JSON.stringify(tool.result, null, 2)}</pre>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )
      })}
    </div>
  )
})

// ─── Message Bubble ───────────────────────────────────────────────────────────

const MessageBubble = React.memo(function MessageBubble({
  message, isLast, conversationId
}: { message: ChatMessage; isLast: boolean; conversationId: string }) {
  const isUser = message.role === 'user'
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null)
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const submitFeedback = async (rating: 'up' | 'down', commentText?: string) => {
    setSubmitting(true)
    try {
      await apiPost('/api/feedback/', {
        conversation_id: conversationId, message_id: message.id,
        rating, comment: commentText || ''
      })
      setFeedback(rating); setSubmitted(true); setShowComment(false)
    } catch { /* silent fail */ }
    finally { setSubmitting(false) }
  }

  return (
    <motion.div
      className={`message-row ${isUser ? 'user' : 'assistant'}`}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {!isUser && (
        <div className="avatar">
          <img src="/logo.svg" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
        </div>
      )}

      <div className={`bubble ${isUser ? 'user-bubble' : 'assistant-bubble'}`}>
        {isUser ? (
          <p>{message.content}</p>
        ) : message.isStreaming && !message.content ? (
          <span className="thinking-indicator">
            <span className="thinking-dot" />
            <span className="thinking-dot" />
            <span className="thinking-dot" />
          </span>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={message.isStreaming ? streamingMarkdownComponents : markdownComponents}
          >
            {message.content}
          </ReactMarkdown>
        )}

        {message.isStreaming && message.content && <span className="cursor-blink">▋</span>}

        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <ToolActivity tools={message.toolCalls} />
        )}

        {/* Feedback */}
        {!isUser && !message.isStreaming && (
          <div className="feedback-row">
            {!submitted ? (
              <>
                <button
                  className={`feedback-btn ${feedback === 'up' ? 'active-up' : ''}`}
                  onClick={() => submitFeedback('up')}
                  disabled={submitting || feedback !== null}
                  title="Good response" aria-label="Good response"
                >
                  <ThumbsUp size={12} />
                </button>
                <button
                  className={`feedback-btn ${feedback === 'down' ? 'active-down' : ''}`}
                  onClick={() => { setFeedback('down'); setShowComment(true) }}
                  disabled={submitting || feedback === 'up'}
                  title="Needs improvement" aria-label="Needs improvement"
                >
                  <ThumbsDown size={12} />
                </button>
              </>
            ) : (
              <span className="feedback-thanks">
                {feedback === 'up' ? '✓ Thanks!' : '✓ Feedback saved'}
              </span>
            )}
          </div>
        )}

        {showComment && (
          <motion.div className="feedback-comment" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>
            <textarea
              placeholder="What was wrong? (optional)" value={comment}
              onChange={e => setComment(e.target.value)} rows={2} autoFocus
            />
            <div className="feedback-comment-actions">
              <Button variant="ghost" size="sm" onClick={() => { setShowComment(false); setFeedback(null) }}>Cancel</Button>
              <Button size="sm" onClick={() => submitFeedback('down', comment)} disabled={submitting}>
                {submitting ? 'Saving...' : 'Submit'}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  )
})

// ─── Doc Upload Pill ──────────────────────────────────────────────────────────

function DocUploadPill({ file, onRemove }: { file: File; onRemove: () => void }) {
  return (
    <motion.div className="doc-pill" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
      <FileJson size={13} />
      <span>{file.name}</span>
      <button onClick={onRemove} aria-label="Remove attachment"><X size={12} /></button>
    </motion.div>
  )
}

function EndpointsFilterPill({ file, appCount, onRemove }: { file: File; appCount: number; onRemove: () => void }) {
  return (
    <motion.div className="doc-pill" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
      <Table size={13} />
      <span>{file.name} · {appCount} app{appCount !== 1 ? 's' : ''}</span>
      <button onClick={onRemove} aria-label="Remove endpoints filter"><X size={12} /></button>
    </motion.div>
  )
}

interface SchemaUserImportSample {
  kind: 'feed_sample' | 'sav_review'
  fileName: string
  delimiter: string
  headers: string[]
  sampleRows: Record<string, string>[]
  rowCountIncluded: number
  note: string
  schemaContent?: string
}

function SchemaUserImportPill({ sample, onRemove }: { sample: SchemaUserImportSample; onRemove: () => void }) {
  return (
    <motion.div className="doc-pill" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
      <Table size={13} />
      <span>{sample.fileName} · {sample.kind === 'sav_review' ? 'SAV review' : `${sample.headers.length} header${sample.headers.length !== 1 ? 's' : ''}`}</span>
      <button onClick={onRemove} aria-label="Remove schema import attachment"><X size={12} /></button>
    </motion.div>
  )
}

function downloadEndpointsFilterTemplate() {
  const csv = [
    'App Name,Group DN (or advanced expression)',
    'ServiceNow,CN=SN-Users,OU=Groups,DC=company,DC=com',
    'ServiceNow,CN=SN-Admins,OU=Groups,DC=company,DC=com',
    'Salesforce,CN=SFDC_%,OU=Groups,DC=company,DC=com',
    'Jira,CN=ACL_Jira_%,OU=Atlassian,OU=Resources,DC=company,DC=com',
  ].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'endpoints_filter_template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Connector starters ───────────────────────────────────────────────────────

type StarterType = 'REST' | 'SOAP' | 'JDBC' | 'AD/LDAP' | 'Identity' | 'Update' | 'Troubleshoot'

interface PromptStarter {
  type: StarterType
  title: string
  desc: string
  bullets: string[]
  concise: string
  full: string
}

const PROMPT_STARTERS: PromptStarter[] = [
  {
    type: 'Identity',
    title: 'Schema user import',
    desc: 'Generate, review, and upload SAV files for file-based identity imports.',
    bullets: ['Header/sample intake', 'SAV file review', 'User attribute mapping', 'SAV upload approval'],
    concise: "I want to import users from a feed file using Saviynt schema user import.\n\nI will upload a small sample CSV or paste the header and first few rows.\nPlease propose the user attribute mapping, ask for the SAV filename and data file prefix, generate the .sav file, and wait for approval before uploading.",
    full: "I want to configure a Saviynt file-based identity import / SchemaUserJob.\n\nFeed details:\n- Data file prefix or name for #FILENAME: [DATA FILE PREFIX]\n- SAV schema file name: [NAME_users.sav]\n- Delimiter: [, / | / tab / ~]\n- Reconciliation field: USERNAME\n\nDefaults are okay unless I say otherwise:\n#USER_NOT_IN_FILE_ACTION=NOACTION\n#SKIP_NUMBER_OF_LINES=1\n#ZERODAYPROVISIONING=FALSE\n#GENERATESYSTEMUSERNAME=FALSE\n#GENERATEEMAIL=FALSE\n#CHECKRULES=FALSE\n#userReconcillationField=USERNAME\n\nI will upload a small sample CSV or paste the header and first few rows. Please review status values, generate STATUSKEYJSON if needed, show the complete .sav content, and wait for my approval before upload."
  },
  {
    type: 'REST',
    title: 'REST API connector',
    desc: 'OpenAPI, Postman, OAuth, API key, imports, and provisioning.',
    bullets: ['Auth and endpoint intake', 'Pagination and response paths', 'Field mapping preview'],
    concise: "I need to configure a Saviynt REST connector for [TARGET SYSTEM].\n\nGoal: [User Import / Account Import / Both / Provisioning]\nAuth: [OAuth2 / Basic / API key / Static bearer / Session]\nDocs: I will upload [OpenAPI / Postman / API reference / sample response].\nConnection name: [NAME]\n\nPlease identify missing inputs, generate the config draft, and show mappings before saving.",
    full: "I need to configure a Saviynt REST connector for [TARGET SYSTEM].\n\nGoal: [User Import / Account Import / Both / Provisioning]\nAuthentication:\n- Type: [OAuth2 client credentials / Basic / API key / Static bearer / Session]\n- Token URL or auth endpoint: [URL if known]\n- Secret names to use: [client_id, client_secret, api_key, etc.]\n\nAPI details:\n- User endpoint: [method + URL]\n- Account endpoint: [method + URL]\n- Entitlement endpoint: [method + URL]\n- Pagination: [offset / page / next URL / cursor / none]\n- Sample response: [paste or upload]\n\nSaviynt:\n- Connection name: [NAME]\n- Import type: [User / Account / Both]\n\nPlease identify missing inputs, generate the JSON draft, show mappings, and wait for approval before saving."
  },
  {
    type: 'SOAP',
    title: 'SOAP connector',
    desc: 'WSDLs, SOAPAction, envelopes, HR/account imports, and provisioning.',
    bullets: ['Endpoint/WSDL capture', 'Request and response samples', 'SOAP JSON field guidance'],
    concise: "I need to configure a Saviynt SOAP connector for [TARGET SYSTEM].\n\nGoal: [HR import / Account import / Provisioning]\nI can provide: [WSDL / SOAP endpoint / SOAPAction / sample envelope / sample response].\nAuth: [WS-Security / Basic / session / custom]\nConnection name: [NAME]\n\nPlease identify missing inputs, use relevant SOAP templates, and show the generated payload before saving.",
    full: "I need to configure a Saviynt SOAP connector for [TARGET SYSTEM].\n\nGoal: [HR import / Account import / Provisioning]\nSOAP details:\n- WSDL or endpoint: [URL]\n- SOAPAction / operation names: [operations]\n- Auth pattern: [WS-Security UsernameToken / Basic / session / custom]\n- Request envelope: [paste or upload]\n- Sample response: [paste or upload]\n\nSaviynt:\n- Connection name: [NAME]\n- Fields needed: [SOAP_ENDPOINT, CONNECTIONJSON, HR_IMPORT_JSON, ACCOUNTS_IMPORT_JSON, provisioning JSONs]\n\nPlease identify missing inputs, map response paths, generate the payload, and wait for approval before saving."
  },
  {
    type: 'JDBC',
    title: 'JDBC/database connector',
    desc: 'Oracle, SQL Server, MySQL, PostgreSQL, DB2, Snowflake, and SQL imports.',
    bullets: ['Driver and URL pattern', 'Import query checklist', 'Network pre-flight'],
    concise: "I need to configure a Saviynt JDBC/database connector for [DATABASE TYPE].\n\nGoal: [User import / Account import / Entitlement import / Provisioning]\nDatabase: [Oracle / SQL Server / MySQL / PostgreSQL / DB2 / Snowflake]\nI have: [host, port, database/schema, driver, import query]\nConnection name: [NAME]\n\nPlease identify missing inputs and confirm network/connectivity assumptions before generating the payload.",
    full: "I need to configure a Saviynt JDBC/database connector for [DATABASE TYPE].\n\nGoal: [User import / Account import / Entitlement import / Provisioning]\nDatabase details:\n- Host: [hostname]\n- Port: [port]\n- Database/schema: [name]\n- Driver/vendor: [Oracle / SQL Server / MySQL / PostgreSQL / DB2 / Snowflake]\n- Driver class if known: [DRIVERNAME]\n- JDBC URL if known: [URL]\n- Import SQL/query: [paste if available]\n\nSaviynt:\n- Connection name: [NAME]\n- Secret names: [db_username, db_password]\n\nPlease identify missing inputs, confirm connectivity requirements, generate the payload, and wait for approval before saving."
  },
  {
    type: 'AD/LDAP',
    title: 'AD/LDAP connector',
    desc: 'LDAPS, bind DN, base DN, object filters, group import, and ENDPOINTS_FILTER.',
    bullets: ['Directory type selection', 'Group scoping support', 'Attribute mapping setup'],
    concise: "I need to configure a Saviynt AD/LDAP connector.\n\nDirectory type: [Active Directory / LDAP]\nGoal: [Account import / Group import / Provisioning / ENDPOINTS_FILTER]\nI have: [LDAPS URL, bind DN, base DN, object filter, group scope]\nConnection name: [NAME]\n\nPlease identify missing inputs and use AD/LDAP-specific fields only.",
    full: "I need to configure a Saviynt AD/LDAP connector.\n\nDirectory details:\n- Type: [Active Directory / LDAP]\n- URL: [ldaps://host:636]\n- Bind DN: [DN]\n- Base DN: [DN]\n- Object filter: [LDAP filter]\n- Group import scope: [all / selected OUs / ENDPOINTS_FILTER]\n- Provisioning needed: [create / update / enable / disable / password / none]\n\nSaviynt:\n- Connection name: [NAME]\n- Secret names: [ldap_bind_password]\n\nPlease identify missing inputs, generate AD/LDAP-specific config, and wait for approval before saving."
  },
  {
    type: 'Update',
    title: 'Update existing connection',
    desc: 'Fetch live config first, preserve unrelated fields, then show a diff.',
    bullets: ['Connection name required', 'Diff-first workflow', 'Approval gate retained'],
    concise: "I need to update an existing Saviynt connection.\n\nConnection name: [EXACT NAME]\nConnector type: [REST / SOAP / JDBC / AD / LDAP]\nChange needed: [describe change]\n\nPlease fetch the current config first, preserve unrelated fields, and show me the diff before saving.",
    full: "I need to update an existing Saviynt connection.\n\nConnection name: [EXACT NAME]\nConnector type: [REST / SOAP / JDBC / AD / LDAP]\nCurrent issue or change:\n- [endpoint changed / add mapping / update auth / add provisioning / fix import]\n\nPlease fetch the current config first, preserve unrelated fields, explain the proposed change, show the generated JSON/diff, and wait for approval before saving."
  },
  {
    type: 'Troubleshoot',
    title: 'Troubleshoot failure',
    desc: 'Start from the connector type, exact error, recent change, and logs.',
    bullets: ['Error-led intake', 'Likely cause ranking', 'Evidence requests'],
    concise: "I need help troubleshooting a Saviynt connector.\n\nConnection name: [EXACT NAME]\nConnector type: [REST / SOAP / JDBC / AD / LDAP]\nError: [paste error]\nRecent change: [if any]\n\nPlease diagnose likely causes and tell me what evidence you need.",
    full: "I need help troubleshooting a Saviynt connector.\n\nConnection name: [EXACT NAME]\nConnector type: [REST / SOAP / JDBC / AD / LDAP]\nFailure point: [connection test / import / provisioning / save]\nError message: [paste exact error]\nRecent change: [if any]\nRelevant logs or sample response: [paste/upload]\n\nPlease fetch current config if needed, rank likely causes, ask for missing evidence, and suggest the safest fix."
  }
]

// ─── Main Chat View ───────────────────────────────────────────────────────────

async function parseEndpointsFilterFile(file: File): Promise<Record<string, string[]>> {
  const ext = file.name.split('.').pop()?.toLowerCase()
  let rows: string[][] = []

  if (ext === 'csv') {
    const Papa = await import('papaparse')
    const text = await file.text()
    const { data } = Papa.default.parse<string[]>(text, { header: false, skipEmptyLines: true })
    rows = data
  } else {
    // .xlsx / .xls — parse with exceljs (no known CVEs)
    const ExcelJS = await import('exceljs')
    const wb = new ExcelJS.default.Workbook()
    const buffer = await file.arrayBuffer()
    await wb.xlsx.load(buffer)
    const ws = wb.worksheets[0]
    ws.eachRow((row) => {
      rows.push([String(row.getCell(1).value ?? ''), String(row.getCell(2).value ?? '')])
    })
  }

  const result: Record<string, string[]> = {}
  for (let i = 1; i < rows.length; i++) {  // skip header row
    const appName = String(rows[i][0] || '').trim()
    const dn = String(rows[i][1] || '').trim()
    if (!appName || !dn) continue
    if (!result[appName]) result[appName] = []
    result[appName].push(dn)
  }
  return result
}

async function parseSchemaUserImportSample(file: File): Promise<SchemaUserImportSample> {
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (ext === 'sav') {
    if (file.size > 1_000_000) throw new Error('SAV files for review must be 1 MB or smaller.')
    const schemaContent = await file.text()
    if (!schemaContent.trim()) throw new Error('The SAV file is empty.')
    return {
      kind: 'sav_review',
      fileName: file.name,
      delimiter: '',
      headers: [],
      sampleRows: [],
      rowCountIncluded: 0,
      schemaContent,
      note: 'Client sent the SAV schema file for review. No source data file was uploaded.',
    }
  }

  if (ext !== 'csv') {
    throw new Error('SchemaUserJob feed samples must be CSV files. Use a .sav file only for schema review.')
  }

  let rows: string[][] = []
  let delimiter = ','

  const Papa = await import('papaparse')
  const text = await file.text()
  const result = Papa.default.parse<string[]>(text, {
    header: false,
    skipEmptyLines: true,
    preview: 6,
  })
  rows = result.data
  delimiter = result.meta.delimiter || ','

  const headers = (rows[0] || []).map(h => String(h || '').trim()).filter(Boolean)
  const sampleRows = rows.slice(1, 6).map(row => {
    const out: Record<string, string> = {}
    headers.forEach((header, idx) => {
      out[header] = String(row[idx] ?? '').trim()
    })
    return out
  })

  return {
    kind: 'feed_sample',
    fileName: file.name,
    delimiter,
    headers,
    sampleRows,
    rowCountIncluded: sampleRows.length,
    note: 'Client parsed only headers and the first few sample rows. Do not request or upload the full production feed.',
  }
}

function inferConnectorType(text: string): StarterType | 'Unknown' {
  const q = text.toLowerCase()
  if (q.includes('schemauserjob') || q.includes('schema user import') || q.includes('identity import') || q.includes('file-based') || q.includes('feed file') || q.includes('sav file')) return 'Identity'
  if (q.includes('soap') || q.includes('wsdl') || q.includes('soapaction')) return 'SOAP'
  if (q.includes('jdbc') || q.includes('database') || q.includes('sql server') || q.includes('oracle') || q.includes('postgres') || q.includes('mysql')) return 'JDBC'
  if (q.includes('active directory') || q.includes('ldap') || q.includes('ldaps') || q.includes('base dn') || q.includes('endpoints_filter')) return 'AD/LDAP'
  if (q.includes('troubleshoot') || q.includes('failing') || q.includes('error')) return 'Troubleshoot'
  if (q.includes('update an existing') || q.includes('existing saviynt connection') || q.includes('fetch the current config')) return 'Update'
  if (q.includes('rest') || q.includes('openapi') || q.includes('postman') || q.includes('oauth') || q.includes('api key')) return 'REST'
  return 'Unknown'
}

function normalizeName(value?: string | null) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function StarterIcon({ type }: { type: StarterType }) {
  const props = { size: 15 }
  if (type === 'REST') return <Globe2 {...props} />
  if (type === 'SOAP') return <FileCode2 {...props} />
  if (type === 'JDBC') return <Database {...props} />
  if (type === 'AD/LDAP') return <Server {...props} />
  if (type === 'Identity') return <Table {...props} />
  if (type === 'Update') return <Wrench {...props} />
  return <AlertTriangle {...props} />
}

function InfoTip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="readiness-info" aria-label={label} type="button">
          <Info size={11} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="readiness-tooltip">
        {children}
      </TooltipContent>
    </Tooltip>
  )
}

function ReadinessPanel({
  project, conversationTitle, messages, input, attachedDoc, endpointsFilterData, schemaUserImportData, configStatus, secrets
}: {
  project?: ChatViewProps['project']
  conversationTitle?: string
  messages: ChatMessage[]
  input: string
  attachedDoc: { file: File; content: string; type: string } | null
  endpointsFilterData: { file: File; parsed: Record<string, any> } | null
  schemaUserImportData: SchemaUserImportSample | null
  configStatus?: string
  secrets: any[]
}) {
  const conversationText = [
    project?.target_system,
    project?.connection_name,
    conversationTitle,
    input,
    ...messages.filter(m => m.role === 'user').slice(-4).map(m => m.content),
  ].filter(Boolean).join('\n')
  const connectorType = inferConnectorType(conversationText)
  const target = project?.target_system || 'Not set'
  const connectionName = project?.connection_name || 'Draft from this chat'
  const targetKey = normalizeName(project?.target_system || project?.connection_name || conversationTitle)
  const connectionKey = normalizeName(project?.connection_name || conversationTitle)
  const secretNames = secrets.map(s => s.secret_name as string)
  const hasSaviyntUsernameSecret = secretNames.includes('saviynt_username')
  const hasSaviyntPassword = secretNames.includes('saviynt_password')
  const hasSaviyntUsername = !!project?.saviynt_username || hasSaviyntUsernameSecret
  const hasSaviyntCredentials = hasSaviyntUsername && hasSaviyntPassword
  const hasDocs = !!attachedDoc || !!endpointsFilterData || !!schemaUserImportData || messages.some(m => m.role === 'user' && /upload|attached|openapi|postman|wsdl|sample response|feed file|schema import/i.test(m.content))
  const hasGeneratedConfig = configStatus && configStatus !== 'draft'

  const classifySecret = (name: string) => {
    const key = normalizeName(name)
    if (name === 'saviynt_password' || name === 'saviynt_username') return 'shared'
    if ((targetKey && key.includes(targetKey)) || (connectionKey && key.includes(connectionKey))) return 'bound'
    if (/(client|secret|token|api|password|username|user|key)/i.test(name)) return 'candidate'
    return 'available'
  }

  const bound = secretNames.filter(n => classifySecret(n) === 'bound')
  const candidates = secretNames.filter(n => classifySecret(n) === 'candidate')
  const missing: string[] = []
  if (!project?.saviynt_url) missing.push('Set Saviynt URL in Project Settings')
  if (!hasSaviyntCredentials) missing.push('Set Saviynt credentials in Project Settings')
  if (!hasDocs && connectorType !== 'JDBC' && connectorType !== 'Update' && connectorType !== 'Troubleshoot') missing.push(connectorType === 'Identity' ? 'Upload or paste a small identity feed sample' : 'Upload docs or sample response to this conversation')
  if (connectorType === 'Unknown') missing.push('State connector type in chat')
  if (candidates.length > 0 && bound.length === 0) missing.push('Confirm target-system secret for this draft')

  const nextAction = missing[0] || (hasGeneratedConfig ? 'Review generated config' : 'Send starter prompt')
  const checks = [
    {
      label: 'Draft scope',
      value: conversationTitle || 'This conversation',
      state: 'done',
      help: 'Pulled from the active conversation title. This keeps readiness scoped to the current chat, not every connection in the project.',
    },
    {
      label: 'Connector type',
      value: connectorType,
      state: connectorType === 'Unknown' ? 'warn' : 'done',
      help: 'Inferred from the active prompt, recent user messages, project target, and connection name. Use a starter or mention REST, SOAP, JDBC, AD, or LDAP to set it clearly.',
    },
    {
      label: 'Target system',
      value: target,
      state: project?.target_system ? 'done' : 'wait',
      help: 'Pulled from Project Settings. Add or edit the target system there so prompts and readiness can classify the draft more accurately.',
    },
    {
      label: 'Connection name',
      value: connectionName,
      state: project?.connection_name ? 'done' : 'wait',
      help: 'Pulled from Project Settings when present. If this project has multiple connection builds, mention the exact connection name in this conversation.',
    },
    {
      label: 'Saviynt URL',
      value: project?.saviynt_url ? 'Configured' : 'Not set',
      state: project?.saviynt_url ? 'done' : 'wait',
      help: 'Pulled from Project Settings. Add the tenant URL there before the agent can fetch, test, or save a connection.',
    },
    {
      label: 'Saviynt credentials',
      value: hasSaviyntCredentials ? 'Configured' : 'Incomplete',
      state: hasSaviyntCredentials ? 'done' : 'warn',
      help: 'Ready when both username and password exist. Username can come from the current Project Settings field or a saviynt_username secret; password must be a saviynt_password secret. Long term, both should be saved as secrets through Project Settings.',
    },
    {
      label: connectorType === 'Identity' ? 'Feed sample' : 'Docs / samples',
      value: hasDocs ? 'Available' : 'Needed',
      state: hasDocs ? 'done' : 'warn',
      help: connectorType === 'Identity'
        ? 'Detected from the identity feed sample attachment or recent chat mentioning feed/schema import. Use only a small header/sample, not the full production file.'
        : 'Detected from current attachments or recent chat mentions such as OpenAPI, Postman, WSDL, upload, or sample response.',
    },
    {
      label: 'Generated config',
      value: hasGeneratedConfig ? configStatus : 'Not yet',
      state: hasGeneratedConfig ? 'done' : 'wait',
      help: 'Pulled from the project config status passed into the chat. Draft means the current conversation has not produced a saved/tested config state yet.',
    },
  ]

  return (
    <aside className="readiness-panel">
      <div className="readiness-header">
        <div>
          <h3>Active draft</h3>
          <p>Scoped to this conversation, not the whole project.</p>
        </div>
        <span className="readiness-type">{connectorType}</span>
      </div>

      <div className="readiness-section">
        <h4>Readiness</h4>
        {checks.map(item => (
          <div key={item.label} className="readiness-row">
            <span className={`readiness-dot ${item.state}`} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <InfoTip label={`How to resolve ${item.label}`}>{item.help}</InfoTip>
          </div>
        ))}
      </div>

      <div className="readiness-section">
        <h4>Secret bindings</h4>
        <div className="secret-bindings-list">
          {secretNames.length === 0 ? (
            <p className="readiness-empty">No project secrets yet.</p>
          ) : secretNames.map(name => {
            const status = classifySecret(name)
            const help = status === 'bound'
              ? 'This secret name appears to match the active target system or connection draft. For production, store an explicit draft-to-secret binding.'
              : status === 'shared'
                ? 'Shared Saviynt credential. It authenticates to the Saviynt tenant and is not assumed to be a target-app credential.'
                : status === 'candidate'
                  ? 'Generic project secret. The agent should ask you to confirm this belongs to the active connection draft before using it.'
                  : 'Available project secret. It is listed for awareness but does not currently match this draft.'
            return (
              <div key={name} className="readiness-row">
                <span className={`readiness-dot ${status === 'bound' || status === 'shared' ? 'done' : status === 'candidate' ? 'wait' : 'idle'}`} />
                <span>{name}</span>
                <strong>{status}</strong>
                <InfoTip label={`Secret binding help for ${name}`}>{help}</InfoTip>
              </div>
            )
          })}
        </div>
      </div>

      <div className="readiness-section">
        <h4>Missing inputs</h4>
        <div className="readiness-chip-row">
          {missing.length ? missing.map(item => (
            <span key={item} className="readiness-chip">
              {item}
              <InfoTip label={`How to resolve ${item}`}>
                {item === 'Confirm target-system secret for this draft'
                  ? 'Tell the agent which existing project secret belongs to this connection draft, or use a future binding UI that maps a secret to a purpose like oauth_client_secret for this draft.'
                  : item === 'Set Saviynt credentials in Project Settings'
                    ? 'Open Project Settings and set Saviynt username and password. The password is stored as a secret; long term the username should be stored as a secret too.'
                    : item === 'Upload or paste a small identity feed sample'
                      ? 'Attach a small CSV sample or paste the header and first few rows. The agent only needs columns and example status values.'
                    : item === 'Upload docs or sample response to this conversation'
                      ? 'Use the attachment control in this chat to upload OpenAPI, Postman, WSDL, text docs, or paste a sample response.'
                      : item === 'State connector type in chat'
                        ? 'Mention REST, SOAP, JDBC, AD, or LDAP in this conversation, or select one of the starter prompts.'
                        : 'Resolve this by adding the missing value in Project Settings or this conversation.'}
              </InfoTip>
            </span>
          )) : (
            <span className="readiness-chip good">
              Ready for next step
              <InfoTip label="Ready for next step">
                The current draft has enough context for the next agent action. Review generated output before approving any save.
              </InfoTip>
            </span>
          )}
        </div>
      </div>

      <div className="readiness-section next-action">
        <h4>
          Next action
          <InfoTip label="How next action is chosen">
            This is the first unresolved readiness item. When nothing is missing, it points you to review generated config or send the starter prompt.
          </InfoTip>
        </h4>
        <p>{nextAction}</p>
      </div>
    </aside>
  )
}

export default function ChatView({ conversationId, projectName, project, conversationTitle, configStatus, onConfigUpdate, onFirstMessage }: ChatViewProps) {
  const [input, setInput] = useState('')
  const [attachedDoc, setAttachedDoc] = useState<{ file: File; content: string; type: string } | null>(null)
  const [endpointsFilterData, setEndpointsFilterData] = useState<{ file: File; parsed: Record<string, any> } | null>(null)
  const [schemaUserImportData, setSchemaUserImportData] = useState<SchemaUserImportSample | null>(null)
  const [showUploadHint, setShowUploadHint] = useState(false)
  const [starterFilter, setStarterFilter] = useState<StarterType | 'All'>('All')
  const [activeStarter, setActiveStarter] = useState<PromptStarter>(PROMPT_STARTERS[0])
  const [secrets, setSecrets] = useState<any[]>([])
  const [, setHistoryLoaded] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const firstMessageFiredRef = useRef(false)

  const { messages, isLoading, activeTools, sendMessage, cancelStream, loadHistory } = useChatStream({
    conversationId, onConfigUpdate
  })

  // Load history
  useEffect(() => {
    apiGet(`/api/conversations/${conversationId}/messages`)
      .then((data: any) => {
        if (Array.isArray(data) && data.length > 0) {
          loadHistory(data.map((m: any) => ({
            id: m.id || crypto.randomUUID(),
            role: m.role as 'user' | 'assistant',
            content: m.content || '',
            toolCalls: m.tool_calls || [],
            timestamp: new Date(m.created_at || Date.now())
          })))
        }
        setHistoryLoaded(true)
      })
      .catch(() => setHistoryLoaded(true))
  }, [conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!project?.id) return
    apiGet(`/api/projects/${project.id}/secrets`)
      .then(data => Array.isArray(data) && setSecrets(data))
      .catch(() => setSecrets([]))
  }, [project?.id])

  useEffect(() => {
    if (firstMessageFiredRef.current) return
    const firstUserMsg = messages.find(m => m.role === 'user')
    if (firstUserMsg && onFirstMessage) {
      firstMessageFiredRef.current = true
      onFirstMessage(firstUserMsg.content)
    }
  }, [messages, onFirstMessage])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, activeTools])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
    }
  }, [input])

  const onDrop = useCallback(async (files: File[]) => {
    const file = files[0]
    if (!file) return
    const content = await file.text()
    const type = file.name.endsWith('.json') || file.name.endsWith('.yaml') || file.name.endsWith('.yml') ? 'openapi' : 'text'
    setAttachedDoc({ file, content, type })
    setShowUploadHint(false)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, noClick: true,
    accept: {
      'application/json': ['.json'],
      'application/x-yaml': ['.yaml', '.yml'],
      'text/plain': ['.txt', '.md'],
      // PDF excluded — browser file.text() extracts binary garbage from PDFs.
      // Re-enable once backend PDF parsing is implemented.
    }
  })

  const handleSend = () => {
    const trimmed = input.trim()
    if (!trimmed && !attachedDoc && !endpointsFilterData && !schemaUserImportData) return
    if (isLoading) return
    const fallbackMessage = schemaUserImportData
      ? schemaUserImportData.kind === 'sav_review'
        ? 'Please review this Saviynt SchemaUserJob SAV file for correctness and required changes.'
        : 'Please use this identity feed sample to configure a Saviynt schema user import and generate the SAV file.'
      : 'Please analyze the attached API document and help me configure a Saviynt connector.'
    sendMessage(trimmed || fallbackMessage, {
      customerApiDoc: attachedDoc?.content,
      customerApiDocType: attachedDoc?.type,
      endpointsFilterData: endpointsFilterData?.parsed,
      schemaUserImportData: schemaUserImportData || undefined,
    })
    setInput('')
    setAttachedDoc(null)
    setEndpointsFilterData(null)
    setSchemaUserImportData(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const isEmpty = messages.length === 0
  const visibleStarters = useMemo(
    () => starterFilter === 'All' ? PROMPT_STARTERS : PROMPT_STARTERS.filter(s => s.type === starterFilter),
    [starterFilter]
  )

  return (
    <div className="chat-view" {...getRootProps()}>
      <input {...getInputProps()} />

      {isDragActive && (
        <div className="drag-overlay">
          <Upload size={32} />
          <p>Drop API documentation here</p>
        </div>
      )}

      <div className="chat-workspace">
        <div className="chat-main-pane">
          {/* Messages */}
          <div className="messages-container">
            {isEmpty && (
              <motion.div className="empty-state-v2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="empty-icon-v2">
                  <img src="/logo.svg" alt="" style={{ width: 32, height: 32, borderRadius: 8 }} />
                </div>
                <h2>Saviynt Configuration Agent</h2>
                <p>
                  Choose a connector-aware starter, or type directly. Starters now cover REST,
                  SOAP, JDBC, AD/LDAP, updates, and troubleshooting without dumping a giant
                  worksheet into chat.
                </p>

                <div className="starter-filter-row">
                  {(['All', 'Identity', 'REST', 'SOAP', 'JDBC', 'AD/LDAP'] as const).map(filter => (
                    <button
                      key={filter}
                      className={`starter-filter ${starterFilter === filter ? 'active' : ''}`}
                      onClick={() => setStarterFilter(filter)}
                    >
                      {filter}
                    </button>
                  ))}
                </div>

                <div className="quick-starts-grid modern">
                  {visibleStarters.map(starter => (
                    <article
                      key={starter.title}
                      className={`quick-start-card modern ${activeStarter.title === starter.title ? 'selected' : ''}`}
                      onClick={() => setActiveStarter(starter)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setActiveStarter(starter)
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-expanded={activeStarter.title === starter.title}
                    >
                      <div className="starter-card-top">
                        <span className="starter-icon"><StarterIcon type={starter.type} /></span>
                        <span className="starter-tag">{starter.type}</span>
                      </div>
                      <div className="quick-start-card-title">{starter.title}</div>
                      <div className="quick-start-card-desc">{starter.desc}</div>
                    </article>
                  ))}
                </div>

                <div className="starter-preview">
                  <div className="starter-preview-head">
                    <div>
                      <strong>Starter preview</strong>
                      <span>{activeStarter.title}</span>
                    </div>
                    <div className="starter-preview-actions">
                      <button onClick={() => setInput(activeStarter.concise)}>Use concise prompt</button>
                      <button onClick={() => setInput(activeStarter.full)}>Use full template</button>
                    </div>
                  </div>
                  <div className="starter-preview-body">
                    <ul className="starter-bullets">
                      {activeStarter.bullets.map(b => <li key={b}>{b}</li>)}
                    </ul>
                    <pre>{activeStarter.concise}</pre>
                  </div>
                </div>
              </motion.div>
            )}

            {messages.map((msg, i) => (
              <MessageBubble key={msg.id} message={msg} isLast={i === messages.length - 1} conversationId={conversationId} />
            ))}

            {/* Live tool activity */}
            {isLoading && activeTools.length > 0 && (
              <div className="message-row assistant">
                <div className="avatar">
                  <img src="/logo.svg" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                </div>
                <div className="live-tools"><ToolActivity tools={activeTools} /></div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input Area */}
          <div className="input-area-v2">
        {attachedDoc && (
          <DocUploadPill file={attachedDoc.file} onRemove={() => setAttachedDoc(null)} />
        )}
        {endpointsFilterData && (
          <EndpointsFilterPill
            file={endpointsFilterData.file}
            appCount={Object.keys(endpointsFilterData.parsed).length}
            onRemove={() => setEndpointsFilterData(null)}
          />
        )}
        {schemaUserImportData && (
          <SchemaUserImportPill
            sample={schemaUserImportData}
            onRemove={() => setSchemaUserImportData(null)}
          />
        )}

        <div className="input-row-v2">
          <button
            className="attach-btn"
            onClick={() => setShowUploadHint(h => !h)}
            title="Attach API documentation"
            aria-label="Attach API documentation"
          >
            <Paperclip size={17} />
          </button>

          <label
            className="attach-btn"
            title="Upload ENDPOINTS_FILTER CSV/Excel (logical apps)"
            aria-label="Upload endpoints filter spreadsheet"
            style={{ cursor: 'pointer' }}
          >
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={async e => {
                const f = e.target.files?.[0]
                if (!f) return
                try {
                  const parsed = await parseEndpointsFilterFile(f)
                  setEndpointsFilterData({ file: f, parsed })
                } catch (err) {
                  console.error('Failed to parse endpoints filter file:', err)
                }
                e.target.value = ''
              }}
            />
            <Table size={17} />
          </label>

          <label
            className="attach-btn"
            title="Upload identity feed sample or SAV file for review"
            aria-label="Upload identity feed sample or SAV file"
            style={{ cursor: 'pointer' }}
          >
            <input
              type="file"
              accept=".csv,.sav"
              style={{ display: 'none' }}
              onChange={async e => {
                const f = e.target.files?.[0]
                if (!f) return
                try {
                  const parsed = await parseSchemaUserImportSample(f)
                  setSchemaUserImportData(parsed)
                } catch (err) {
                  console.error('Failed to parse identity feed sample:', err)
                }
                e.target.value = ''
              }}
            />
            <Database size={17} />
          </label>

          <textarea
            ref={textareaRef}
            className="chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={attachedDoc ? "Add a message about this doc, or press send..." : endpointsFilterData ? "Add a message about this endpoints filter, or press send..." : schemaUserImportData ? "Add the SAV filename/data prefix, or press send..." : "Describe what you want to configure..."}
            rows={1}
          />

          <button
            className={`send-btn ${isLoading ? 'stop' : ''}`}
            onClick={isLoading ? cancelStream : handleSend}
            disabled={!isLoading && !input.trim() && !attachedDoc && !endpointsFilterData && !schemaUserImportData}
            aria-label={isLoading ? 'Stop generation' : 'Send message'}
          >
            {isLoading ? <Square size={15} fill="currentColor" /> : <Send size={15} />}
          </button>
        </div>

        <p className="chat-disclaimer">
          AI can make mistakes — if something looks off, correct it by providing feedback in the chat.
        </p>

        <AnimatePresence>
          {showUploadHint && (
            <motion.div
              className="upload-hint"
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
            >
              <p>Drag &amp; drop or click to attach:</p>
              <ul>
                <li><strong>OpenAPI / Swagger</strong> (.json, .yaml)</li>
                <li><strong>Postman Collection</strong> (.json)</li>
                <li><strong>API Documentation</strong> (.txt)</li>
              </ul>
              <label className="upload-label">
                <input type="file" accept=".json,.yaml,.yml,.txt"
                  onChange={e => { const f = e.target.files?.[0]; if (f) onDrop([f]) }} />
                <Upload size={13} /> Browse files
              </label>
              <hr style={{ margin: '8px 0', borderColor: 'var(--border)' }} />
              <p style={{ marginBottom: 4 }}><Table size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /><strong>Logical Apps / ENDPOINTS_FILTER</strong></p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Upload a CSV or Excel file to define app-to-group mappings for the AD/LDAP Security System.
              </p>
              <button
                className="upload-label"
                onClick={downloadEndpointsFilterTemplate}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <Download size={13} /> Download template
              </button>
              <hr style={{ margin: '8px 0', borderColor: 'var(--border)' }} />
              <p style={{ marginBottom: 4 }}><Database size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /><strong>Identity Feed Sample / SAV Review</strong></p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Upload a small CSV sample for SchemaUserJob SAV generation, or a SAV file for review. Only feed headers and sample rows are sent; the SAV file is sent as schema text.
              </p>
              <label className="upload-label">
                <input type="file" accept=".csv,.sav"
                  onChange={async e => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    try {
                      setSchemaUserImportData(await parseSchemaUserImportSample(f))
                      setShowUploadHint(false)
                    } catch (err) {
                      console.error('Failed to parse identity feed sample:', err)
                    }
                  }} />
                <Upload size={13} /> Browse file
              </label>
            </motion.div>
          )}
        </AnimatePresence>
          </div>
        </div>

        <ReadinessPanel
          project={project}
          conversationTitle={conversationTitle}
          messages={messages}
          input={input}
          attachedDoc={attachedDoc}
          endpointsFilterData={endpointsFilterData}
          schemaUserImportData={schemaUserImportData}
          configStatus={configStatus}
          secrets={secrets}
        />
      </div>
    </div>
  )
}
